import { z } from 'zod';
import { ATTACHMENT_KINDS, CATALOG_KINDS, CONTRACT_SIGN_FORMS, PRESENTATIONS, PRICE_ROUNDINGS, VAT_PROFILES, VAT_RATES } from './offers';
import './schemas';

const keys = <T extends Record<string, unknown>>(o: T) => Object.keys(o) as [keyof T & string, ...(keyof T & string)[]];
const str = (max: number) => z.string().trim().max(max);
const gr = z.number().int('Kwota w groszach').min(0, 'Kwota nie może być ujemna').max(100_000_000_00);
const vatRate = z.number().refine((v) => (VAT_RATES as readonly number[]).includes(v), 'Niedozwolona stawka VAT');
const qty = z.number().positive('Ilość musi być większa od zera').max(1_000_000);

export const discountSchema = z
  .object({ type: z.enum(['procent', 'kwota']), value: z.number().min(0) })
  .refine((d) => d.type === 'kwota' || d.value <= 100, 'Rabat procentowy nie może przekraczać 100%')
  .nullable();

export const catalogPackInputSchema = z.object({
  id: z.string().uuid().nullable().default(null),
  name: str(60).min(1, 'Podaj nazwę opakowania'),
  /** ile jednostek magazynowych w opakowaniu (1 krążek = 100 m) */
  factor: z.number().positive('Przelicznik musi być większy od zera').max(1_000_000),
  ean: str(20).default(''),
  active: z.boolean().default(true),
});
export type CatalogPackInput = z.infer<typeof catalogPackInputSchema>;

export const catalogItemInputSchema = z.object({
  kind: z.enum(keys(CATALOG_KINDS)),
  /** branże produktu (puste = wspólny dla wszystkich) */
  tradeCodes: z.array(str(4).min(1)).max(20).default([]),
  /** zgodność wstecz: jedna branża — dopisywana do tradeCodes */
  tradeCode: str(4).nullable().optional(),
  groupId: z.string().uuid().nullable().default(null),
  ean: str(20).default(''),
  /** prowadzić stan magazynowy (domyślnie: urządzenie i materiał tak; usługa i pakiet — nigdy) */
  stocked: z.boolean().optional(),
  hideInOffers: z.boolean().default(false),
  leadTimeDays: z.number().int().min(0).max(365).nullable().default(null),
  packs: z.array(catalogPackInputSchema).max(10).default([]),
  code: str(40).default(''),
  name: str(200).min(2, 'Podaj nazwę'),
  description: str(4000).default(''),
  manufacturer: str(100).default(''),
  model: str(100).default(''),
  unit: str(10).min(1),
  vatRate,
  /** null = nie zmieniaj (np. użytkownik nie widzi cen) */
  salePriceGr: gr.nullable(),
  purchasePriceGr: gr.nullable(),
  forInstallation: z.boolean().default(false),
  /** docs/26: pozycja cennika usług serwisowych (do wyboru na telefonie serwisanta) */
  servicePrice: z.boolean().default(false),
  active: z.boolean().default(true),
  components: z.array(z.object({ itemId: z.string().uuid(), quantity: qty })).default([]),
  /** stała marża: cena sprzedaży liczona z ceny zakupu (null = cena sprzedaży wpisywana ręcznie) */
  targetMarginPct: z.number().min(-99, 'Marża co najmniej −99%').max(99.9, 'Marża musi być mniejsza niż 100%').nullable().default(null),
  priceRounding: z.enum(keys(PRICE_ROUNDINGS)).default('brak'),
});
export type CatalogItemInput = z.infer<typeof catalogItemInputSchema>;

/** Świadomy zapis ceny bazowej na karcie produktu (np. podanej w kalkulacji oferty) — docs/14 §2.2. */
export const catalogBasePriceSchema = z.object({
  purchasePriceGr: gr,
  source: z.enum(['reczna', 'oferta']).default('reczna'),
  note: str(300).default(''),
});
export type CatalogBasePriceInput = z.infer<typeof catalogBasePriceSchema>;

export const catalogGroupInputSchema = z.object({
  name: str(80).min(2, 'Podaj nazwę grupy'),
  sortOrder: z.number().int().min(0).max(9999).default(0),
  active: z.boolean().default(true),
});
export type CatalogGroupInput = z.infer<typeof catalogGroupInputSchema>;

export const attachmentMetaSchema = z.object({
  kind: z.enum(keys(ATTACHMENT_KINDS)),
  title: str(200).default(''),
  forInstallation: z.boolean().default(false),
});

export const offerCreateSchema = z.object({
  caseId: z.string().uuid(),
  scopeIds: z.array(z.string().uuid()).min(1, 'Wybierz co najmniej jeden zakres prac'),
  /** kopia treści z innej wersji (np. po zmianie firmy) */
  copyFromVersionId: z.string().uuid().nullable().default(null),
});

const component = z.object({ name: str(200), quantity: qty, unit: str(10) });

export const offerItemInputSchema = z.object({
  /** identyfikator istniejącej pozycji — zachowuje migawkę ceny katalogowej i kosztu */
  id: z.string().uuid().nullable().default(null),
  scopeId: z.string().uuid(),
  catalogItemId: z.string().uuid().nullable().default(null),
  name: str(300).min(1, 'Pozycja musi mieć nazwę'),
  description: str(2000).default(''),
  unit: str(10).min(1),
  quantity: qty,
  unitPriceGr: gr,
  discount: discountSchema.default(null),
  vatRate,
  /** tylko dla pozycji własnych i tylko z uprawnieniem do cen zakupu */
  purchasePriceGr: gr.nullable().default(null),
  components: z.array(component).max(50).default([]),
  /** ustaw cenę i migawkę według aktualnego cennika */
  refreshFromCatalog: z.boolean().default(false),
});
export type OfferItemInput = z.infer<typeof offerItemInputSchema>;

export const offerVersionSaveSchema = z.object({
  title: str(200).min(3, 'Tytuł oferty musi mieć co najmniej 3 znaki'),
  validUntil: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Data w formacie RRRR-MM-DD'),
  terms: str(6000).default(''),
  changeNote: str(1000).default(''),
  vatProfile: z.enum(keys(VAT_PROFILES)),
  variants: z
    .array(
      z.object({
        name: str(120).min(1, 'Wariant musi mieć nazwę'),
        description: str(2000).default(''),
        presentation: z.enum(keys(PRESENTATIONS)),
        discount: discountSchema.default(null),
        items: z.array(offerItemInputSchema).max(300),
      }),
    )
    .min(1, 'Oferta musi mieć co najmniej jeden wariant')
    .max(6, 'Maksymalnie 6 wariantów'),
});
export type OfferVersionSaveInput = z.infer<typeof offerVersionSaveSchema>;

export const reasonSchema = z.object({ reason: str(500).min(3, 'Podaj powód') });

export const manualAcceptanceSchema = z.object({
  versionId: z.string().uuid(),
  variantId: z.string().uuid(),
  method: z.enum(['telefon', 'papier', 'email']),
  personName: str(120).min(3, 'Podaj, kto zaakceptował'),
  note: str(1000).default(''),
});

export const shareCreateSchema = z.object({
  versionIds: z.array(z.string().uuid()).min(1, 'Wybierz co najmniej jedną ofertę'),
  recipientEmail: z.string().trim().toLowerCase().email('Niepoprawny adres e-mail'),
  sendEmail: z.boolean().default(true),
  message: str(2000).default(''),
});

export const publicAcceptRequestSchema = z.object({
  versionId: z.string().uuid(),
  variantId: z.string().uuid(),
  personName: str(120).min(3, 'Podaj imię i nazwisko'),
});

export const publicRejectSchema = z.object({
  versionId: z.string().uuid(),
  personName: str(120).min(3, 'Podaj imię i nazwisko'),
  reasonCode: z.enum(['cena', 'zakres', 'termin', 'inna_oferta', 'rezygnacja', 'inny']),
  reason: str(1000).default(''),
});

/** Prośba klienta o zmiany z linku (QA P15) — jak „klient prosi o zmiany” zanotowane w biurze. */
export const publicChangesSchema = z.object({
  versionId: z.string().uuid(),
  personName: str(120).min(3, 'Podaj imię i nazwisko'),
  note: str(2000).min(3, 'Opisz, co chcesz zmienić'),
});

export const publicAcceptConfirmSchema = publicAcceptRequestSchema.extend({
  code: z.string().trim().max(10).default(''),
  consent: z.literal(true, { error: 'Potwierdź oświadczenie' }),
});

/** Dopisanie swoich zakresów do niewydanej oferty innej osoby (oferta wspólna). */
export const offerJoinSchema = z.object({ scopeIds: z.array(z.string().uuid()).min(1).max(20) });
export const offerReadySchema = z.object({ ready: z.boolean() });

/** Wydanie oferty; przy przekroczonych progach (Z-14) — uzasadnienie wyjątku zapisywane w historii. */
export const offerIssueSchema = z.object({ exceptionReason: str(1000).default('') }).default({ exceptionReason: '' });
/** Prośba o wydanie oferty z wyjątkiem (rabat / marża ponad próg) — do osób z prawem wydania poniżej progu. */
export const offerIssueRequestSchema = z.object({ note: str(1000).default('') }).default({ note: '' });
/** Odrzucenie prośby o wydanie z wyjątkiem — komentarz dla handlowca obowiązkowy. */
export const offerIssueRejectSchema = z.object({ comment: z.string().trim().min(3, 'Napisz handlowcowi, co poprawić').max(1000) });

/**
 * Ustalenia do umowy (QA P33) — wewnętrzne, na poziomie oferty (nie zmieniają wydanej treści): zaliczka w %,
 * forma podpisu i uwagi dla działu umów. Przy przygotowaniu umowy zaliczka zastępuje domyślną z ustawień firmy.
 */
export const offerContractTermsSchema = z.object({
  /** null — jak w ustawieniach firmy; 0 — bez zaliczki */
  depositPct: z.number().int('Zaliczka w pełnych procentach').min(0).max(100).nullable().default(null),
  signForm: z.enum(keys(CONTRACT_SIGN_FORMS)).nullable().default(null),
  note: str(1000).default(''),
});
export type OfferContractTerms = z.infer<typeof offerContractTermsSchema>;
